import React from 'react';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';

import { Alert } from '@mui/material';

import Record from 'ui/Record/Record';
import MainHeader from 'components/MainHeader';
import { Button, SecondaryButton } from 'components/Button';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { getErrorMessage } from 'utils/api/errors';

import routes from 'core/routes';

import { findUnit, isIntake, unitPathIds } from './types';

import UnitTree from './components/UnitTree';
import UnitPanel from './components/UnitPanel';
import UnitRowMenu from './components/UnitRowMenu';
import SettingsCard from './components/SettingsCard';
import { useUnitTreeView } from './components/useUnitTreeView';
import UnitDialogs from './components/UnitDialogs';
import { RetryRow, SkeletonRow, SplitLayout, TreeGuidance, TreeScroll } from './components/styles';

const OrganisationStructurePage: React.FunctionComponent = () => {
  const navBarHeight = useNavBarHeight();
  const { notification, organisationStructure } = useStore();

  const [addingUnder, setAddingUnder] = React.useState<string | null>(null);
  const [moving, setMoving] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);

  React.useEffect(() => {
    organisationStructure.loadUnits();
  }, [organisationStructure]);

  const units = organisationStructure.tree;
  const surfaceState = organisationStructure.surfaceState;
  const topId = units[0]?.uuid ?? '';
  const onlySystemUnits = (units[0]?.children ?? []).every(isIntake);
  const {
    expandedIds,
    toggleExpand,
    expand,
    selectedId,
    selected,
    select,
    closePanel,
    regionRef,
    onRegionKeyDown,
    focusRow,
  } = useUnitTreeView(units);

  // Every refusal from the API carries a sentence written for the customer, so it is shown
  // as it came rather than replaced with something of ours.
  const refuse = (error: unknown, fallback: string): void =>
    notification.enqueueErrorSnackbar(getErrorMessage(error as AxiosError, fallback));

  // A unit lands inside its new ancestors, and a collapsed ancestor would hide it.
  const expandPathTo = (unitId: string): void => unitPathIds(units, unitId).forEach(expand);

  const handleAddUnit = async (name: string, parentId: string): Promise<void> => {
    const parentName = findUnit(units, parentId)?.name ?? 'the top unit';
    try {
      await organisationStructure.createUnit(parentId, name);
    } catch (error) {
      refuse(error, 'Could not add the unit');
      return;
    }
    expand(parentId);
    setAddingUnder(null);
    notification.enqueueSuccessSnackbar(`${name} added under ${parentName}.`);
  };

  const handleMoveConfirm = async (newParentId: string): Promise<void> => {
    if (!moving) return;
    const name = findUnit(units, moving)?.name ?? 'Unit';
    const parentName = findUnit(units, newParentId)?.name ?? '';
    try {
      await organisationStructure.moveUnit(moving, newParentId);
    } catch (error) {
      refuse(error, 'Could not move the unit');
      return;
    }
    expandPathTo(moving);
    setMoving(null);
    notification.enqueueSuccessSnackbar(`${name} moved under ${parentName}.`);
  };

  const handleDelete = async (destinationId: string): Promise<void> => {
    if (!deleting) return;
    const name = findUnit(units, deleting)?.name ?? 'Unit';
    try {
      await organisationStructure.deleteUnit(deleting, destinationId);
    } catch (error) {
      refuse(error, 'Could not delete the unit');
      return;
    }
    if (selectedId === deleting) select(destinationId);
    expandPathTo(destinationId);
    setDeleting(null);
    notification.enqueueSuccessSnackbar(`${name} deleted.`);
  };

  const handleEdit = async (name: string): Promise<void> => {
    if (!editing) return;
    try {
      await organisationStructure.renameUnit(editing, name);
    } catch (error) {
      refuse(error, 'Could not rename the unit');
      return;
    }
    setEditing(null);
    notification.enqueueSuccessSnackbar(`Unit renamed to ${name}.`);
  };

  const unitActions = (unitId: string) => ({
    onAddUnder: () => setAddingUnder(unitId),
    onEdit: () => setEditing(unitId),
    onMove: () => setMoving(unitId),
    onDelete: () => setDeleting(unitId),
  });

  const renderTree = (): React.ReactElement => (
    <UnitTree
      units={units}
      selectedId={selectedId}
      onSelect={select}
      expandedIds={expandedIds}
      onToggleExpand={toggleExpand}
      countFor={(unit) => unit.userCount}
      // Intake cannot be renamed, moved or deleted, so it has no actions.
      renderMenu={(unit) => (isIntake(unit) ? null : <UnitRowMenu unit={unit} {...unitActions(unit.uuid)} />)}
    />
  );

  const renderBody = (): React.ReactElement => {
    if (surfaceState === 'loading') {
      return (
        <SettingsCard title="Units">
          <SkeletonRow />
          <SkeletonRow $depth={1} />
          <SkeletonRow $depth={1} />
          <SkeletonRow $depth={2} />
          <SkeletonRow $depth={2} />
        </SettingsCard>
      );
    }

    if (surfaceState === 'error') {
      return (
        <SettingsCard title="Units">
          <Alert severity="error">Could not load your organisation structure.</Alert>
          <RetryRow>
            <SecondaryButton size="medium" onClick={() => organisationStructure.loadUnits()}>
              Try again
            </SecondaryButton>
          </RetryRow>
        </SettingsCard>
      );
    }

    return (
      <>
        <SplitLayout $panelOpen={!!selected} ref={regionRef} onKeyDown={onRegionKeyDown}>
          <SettingsCard title="Units" data-testid="unit-tree">
            <TreeScroll>
              {renderTree()}
              {onlySystemUnits ? (
                <TreeGuidance>
                  {`Add units under ${units[0]?.name} to show how your organisation is divided, such as by division or area.`}
                </TreeGuidance>
              ) : null}
            </TreeScroll>
          </SettingsCard>
          {selected ? <UnitPanel units={units} unit={selected} onClose={closePanel} /> : null}
        </SplitLayout>
      </>
    );
  };

  return (
    <>
      <Record
        navBarHeight={navBarHeight}
        Header={
          <MainHeader
            title="Organisation Structure"
            breadcrumbs={[
              { label: 'Organisation', link: '' },
              { label: 'Organisation Structure', link: routes.dashboard.settings.organisationStructure.toString() },
            ]}
            action={
              surfaceState === 'default' ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setAddingUnder(selected && !isIntake(selected) ? selected.uuid : topId)}
                >
                  Add unit
                </Button>
              ) : undefined
            }
          />
        }
        Content={() => <div data-testid="organisation-structure-page">{renderBody()}</div>}
      />

      <UnitDialogs
        units={units}
        topId={topId}
        expandedIds={expandedIds}
        addingUnder={addingUnder}
        editing={editing}
        moving={moving}
        deleting={deleting}
        // Closing a dialog puts focus back on the row that opened it, rather than dropping it
        // to the body and losing a keyboard user's place in the tree.
        onCancelAdd={() => {
          focusRow(addingUnder);
          setAddingUnder(null);
        }}
        onCancelEdit={() => {
          focusRow(editing);
          setEditing(null);
        }}
        onCancelMove={() => {
          focusRow(moving);
          setMoving(null);
        }}
        onCancelDelete={() => {
          focusRow(deleting);
          setDeleting(null);
        }}
        onAdd={handleAddUnit}
        onEdit={handleEdit}
        onMove={handleMoveConfirm}
        onDelete={handleDelete}
      />
    </>
  );
};

export default observer(OrganisationStructurePage);
